import {
  createDecryptStream,
  decryptFileMeta,
  decryptManifest,
  deriveFileKey,
  deriveManifestKey,
  parseFragment,
  pbkdf2Kdf,
  unwrapMasterKey,
  type FileMeta,
  type Fragment,
  type Manifest,
} from "@limeshare/lsc1";
import type { AccessResponse } from "@limeshare/shared";
import { api } from "../api";
import { argon2idKdf } from "./kdf";
import { chooseSink, type Sink } from "./sinks";

export interface OpenedFile {
  fileId: string;
  encryptedSize: number;
  downloadUrl: string;
  meta: FileMeta;
}

export interface OpenedTransfer {
  transferId: string;
  manifest: Manifest;
  files: OpenedFile[];
  burn: boolean;
  expiresAt: number | null;
  claimGraceUntil: number | null;
  downloadsRemaining: number | null;
  token: string;
  masterKey: Uint8Array;
  paywallPriceUsd: number | null;
  paywallUnlocked: boolean;
}

export class NeedsPasswordError extends Error {
  constructor(readonly fragment: Extract<Fragment, { kind: "password" }>) {
    super("Password required");
    this.name = "NeedsPasswordError";
  }
}

/** Parses the URL fragment; never sends it anywhere. */
export function readFragment(): Fragment {
  return parseFragment(window.location.hash);
}

/** Resolves the master key from the fragment (unwrapping with the password when needed). */
export async function resolveMasterKey(fragment: Fragment, transferId: string, password?: string): Promise<Uint8Array> {
  if (fragment.kind === "plain") return fragment.key;
  if (password === undefined) throw new NeedsPasswordError(fragment);
  const kdf = fragment.kdf === "argon2id" ? argon2idKdf : pbkdf2Kdf;
  const kek = await kdf(password, fragment.salt);
  return unwrapMasterKey(fragment.wrappedKey, kek, transferId);
}

/** §9 steps 1–3: authorize with the token, decrypt manifest + per-file meta, verify declared sizes. */
export async function openTransfer(
  transferId: string,
  fragment: Fragment,
  password?: string,
  turnstileToken?: string,
): Promise<OpenedTransfer> {
  const masterKey = await resolveMasterKey(fragment, transferId, password);
  const access: AccessResponse = await api.access(transferId, fragment.token, turnstileToken);

  const manifestKey = await deriveManifestKey(masterKey, transferId);
  const manifest = await decryptManifest(manifestKey, transferId, access.encryptedManifest);

  const files: OpenedFile[] = [];
  for (const f of access.files) {
    const fileKey = await deriveFileKey(masterKey, transferId, f.fileId);
    const meta = f.encryptedFileMeta
      ? await decryptFileMeta(fileKey, transferId, f.fileId, f.encryptedFileMeta)
      : manifestEntry(manifest, f.fileId);
    files.push({ fileId: f.fileId, encryptedSize: f.encryptedSize, downloadUrl: f.downloadUrl, meta });
  }

  return {
    transferId,
    manifest,
    files,
    burn: access.burn,
    expiresAt: access.expiresAt,
    claimGraceUntil: access.claimGraceUntil,
    downloadsRemaining: access.downloadsRemaining,
    token: fragment.token,
    masterKey,
    paywallPriceUsd: access.paywallPriceUsd ?? null,
    paywallUnlocked: Boolean(access.paywallUnlocked),
  };
}

function manifestEntry(manifest: Manifest, fileId: string): FileMeta {
  const e = manifest.files.find((x) => x.id === fileId);
  if (!e) throw new Error(`File ${fileId} is not listed in the manifest`);
  return { name: e.name, size: e.size, type: e.type, ...(e.mtime !== undefined ? { mtime: e.mtime } : {}) };
}

export interface DownloadProgress {
  bytesDone: number;
  bytesTotal: number;
}

/**
 * §9 step 4: fetch ciphertext straight from storage, decrypt as a stream, write to the chosen sink.
 * `sink` must be obtained via chooseSink() inside the click handler.
 */
export async function downloadFile(
  t: OpenedTransfer,
  file: OpenedFile,
  sink: Sink,
  onProgress: (p: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  const fileKey = await deriveFileKey(t.masterKey, t.transferId, file.fileId);
  const res = await fetch(file.downloadUrl, { signal: signal ?? null, mode: "cors" });
  if (!res.ok || !res.body) {
    throw new Error(res.status === 403 ? "Download link expired — reload the page to get a fresh one." : `Storage error ${res.status}`);
  }
  const decrypt = createDecryptStream({
    key: fileKey,
    expectedEncryptedSize: file.encryptedSize,
    onProgress: (done) => onProgress({ bytesDone: done, bytesTotal: file.meta.size }),
  });
  try {
    await sink.write(res.body.pipeThrough(decrypt));
  } catch (e) {
    await sink.abort();
    throw e;
  }
}

export async function reportDownloadComplete(t: OpenedTransfer): Promise<void> {
  if (!t.burn) return;
  try {
    await api.completeDownload(t.transferId, t.token);
  } catch {
    /* the grace window will purge it anyway */
  }
}

export { chooseSink };
